/* Client-side PDF rasterization for floor-plan uploads (Path A: mirror durham-om).

   durham-om never rendered PDFs — its "drawings" were JPEGs converted offline. We reproduce that
   experience by rasterizing each PDF page to a high-resolution JPEG at upload time, so the plan
   viewer, pins and storage all stay on the existing raster-image path. Nothing here runs while
   viewing a plan; it runs once, on upload.

   The legacy pdfjs build is loaded (see declarations.d.ts) because field techs carry older iPads;
   its worker and standard-font data are served from public/ (scripts/sync-pdf-assets.js). */

/* pdf.js is ~120KB gzipped and only needed when a PDF is actually uploaded, so it is loaded lazily
   via dynamic import(): webpack splits it into its own chunk that never touches the main bundle.
   isPdfFile() below stays static and cheap so the upload path can branch without pulling pdf.js. */
const PUBLIC = process.env.PUBLIC_URL || '';

type PdfjsModule = typeof import('pdfjs-dist/legacy/build/pdf');
let pdfjsPromise: Promise<PdfjsModule> | null = null;

function loadPdfjs(): Promise<PdfjsModule> {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist/legacy/build/pdf').then(pdfjsLib => {
      pdfjsLib.GlobalWorkerOptions.workerSrc = `${PUBLIC}/pdf.worker.min.mjs`;
      return pdfjsLib;
    });
  }
  return pdfjsPromise;
}

/* Target ~2800px on the long edge — the same fidelity uploadFacilityPlan keeps for raster plans,
   enough to read room numbers when zoomed. Capped so neither dimension exceeds the canvas limit
   older iOS Safari enforces (~4096px), which would otherwise render blank. */
const TARGET_LONG_EDGE = 2800;
const MAX_CANVAS_DIM = 4000;
const JPEG_QUALITY = 0.85;

export interface RasterPage {
  pageNumber: number;
  blob: Blob;
}

/** True when the picked file is a PDF (by MIME, with a filename fallback for iOS quirks). */
export const isPdfFile = (file: File): boolean =>
  file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

/** Renders every page of a PDF to a JPEG blob, invoking `onPage` as each finishes so the caller
 *  can upload page-by-page and report progress. Returns the total page count. */
export async function rasterizePdf(
  file: File,
  onPage: (page: RasterPage, total: number) => Promise<void> | void,
): Promise<number> {
  const pdfjsLib = await loadPdfjs();
  const data = new Uint8Array(await file.arrayBuffer());
  const pdf = await pdfjsLib.getDocument({
    data,
    standardFontDataUrl: `${PUBLIC}/standard_fonts/`,
    isEvalSupported: false,
  }).promise;

  try {
    const total = pdf.numPages;
    for (let n = 1; n <= total; n++) {
      const page = await pdf.getPage(n);
      try {
        const base = page.getViewport({ scale: 1 });
        let scale = TARGET_LONG_EDGE / Math.max(base.width, base.height);
        // Clamp so neither rendered dimension exceeds the iOS canvas limit.
        scale = Math.min(scale, MAX_CANVAS_DIM / base.width, MAX_CANVAS_DIM / base.height);
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not get a 2D canvas context for PDF rendering.');
        // PDFs are transparent; without a white ground the JPEG comes out black.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        await page.render({ canvasContext: ctx, viewport }).promise;

        const blob = await new Promise<Blob>((resolve, reject) =>
          canvas.toBlob(
            b => (b ? resolve(b) : reject(new Error('Failed to encode PDF page as an image.'))),
            'image/jpeg',
            JPEG_QUALITY,
          ),
        );
        await onPage({ pageNumber: n, blob }, total);

        // Release the bitmap promptly — a big drawing set would otherwise pile up in memory.
        canvas.width = 0;
        canvas.height = 0;
      } finally {
        page.cleanup();
      }
    }
    return total;
  } finally {
    await pdf.destroy();
  }
}
